import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  Tooltip,
  ListItemIcon,
  Alert,
  Stack,
  Fade,
  Grow,
  Chip,
  LinearProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon,
  DoneAll as DoneAllIcon,
  NotificationsNone as NotificationsNoneIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import {
  StyledMenu,
  NotificationHeader,
  NotificationItem,
  StyledBadge,
  EmptyStateContainer,
  NotificationContent
} from './styles';

interface NotificationsProps {
  onNotificationCountChange?: (count: number) => void;
}

const Notifications: React.FC<NotificationsProps> = ({ onNotificationCountChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    notifications,
    unreadCount,
    loading,
    error,
    loadUnreadCount,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    handleNotificationAction
  } = useNotifications();

  // NÃO carrega automaticamente - apenas quando usuário abre o menu
  // useEffect removido para evitar chamadas desnecessárias

  // Notify parent component about unread count changes
  useEffect(() => {
    if (onNotificationCountChange) {
      onNotificationCountChange(unreadCount);
    }
  }, [unreadCount, onNotificationCountChange]);

  // Load full notifications when menu opens
  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    await refreshNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRefreshClick = async () => {
    setIsRefreshing(true);
    await refreshNotifications();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleNotificationActionLocal = async (notificationId: string, action: 'accept' | 'reject') => {
    try {
      setProcessingId(notificationId);
      
      const notification = Array.isArray(notifications) ? notifications.find(n => n.id === notificationId) : null;
      await handleNotificationAction(notificationId, action);

      // Show success message and handle navigation
      if (action === 'accept') {
        if (notification?.projectInvite) {
          setTimeout(() => {
            navigate(`/project/${notification.projectInvite!.projectId}`);
            handleClose();
          }, 1000);
        } else if (notification?.boardInvite) {
          setTimeout(() => {
            handleClose();
          }, 1000);
        } else {
          handleClose();
        }
      } else {
      }
    } catch (error) {
      console.error('Failed to handle notification action:', error);
      alert('Erro ao processar a notificação. Tente novamente.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleMarkAsReadLocal = async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      alert('Erro ao marcar notificação como lida.');
    }
  };

  const handleMarkAllAsReadLocal = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      alert('Erro ao marcar todas como lidas.');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'ProjectInvite':
        return <GroupIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
      case 'BoardInvite':
        return <DashboardIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />;
      case 'System':
        return <NotificationsIcon fontSize="small" sx={{ color: theme.palette.info.main }} />;
      case 'General':
        return <NotificationsIcon fontSize="small" sx={{ color: theme.palette.success.main }} />;
      default:
        return <NotificationsIcon fontSize="small" />;
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    switch (type) {
      case 'ProjectInvite':
        return 'Convite de Projeto';
      case 'BoardInvite':
        return 'Convite de Quadro';
      case 'System':
        return 'Sistema';
      case 'General':
        return 'Geral';
      default:
        return 'Notificação';
    }
  };

  const formatNotificationDate = (dateString: string) => {
    try {
      const result = formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: ptBR
      });
      return String(result);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Data inválida';
    }
  };

  const unreadNotifications = Array.isArray(notifications) 
    ? notifications.filter(n => !n.isRead).length 
    : 0;

  return (
    <>
      <Tooltip title={unreadCount > 0 ? `${unreadCount} notificações não lidas` : 'Notificações'}>
        <IconButton
          aria-label="notifications"
          onClick={handleClick}
          sx={{ 
            color: 'white',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.1)',
            }
          }}
        >
          <StyledBadge 
            badgeContent={unreadCount} 
            color="error"
            invisible={unreadCount === 0}
            max={99}
          >
            <NotificationsIcon />
          </StyledBadge>
        </IconButton>
      </Tooltip>

      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        TransitionComponent={Fade}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <NotificationHeader>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Notificações
            </Typography>
            {unreadNotifications > 0 && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {unreadNotifications} não lida{unreadNotifications > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {unreadNotifications > 0 && (
              <Tooltip title="Marcar todas como lidas">
                <IconButton
                  size="small"
                  onClick={handleMarkAllAsReadLocal}
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? theme.palette.text.primary : 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Atualizar">
              <IconButton
                size="small"
                onClick={handleRefreshClick}
                disabled={loading || isRefreshing}
                sx={{ 
                  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : 'white',
                  '&:hover': { 
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'scale(1.1)'
                  },
                  animation: isRefreshing ? 'spin 0.5s ease-in-out' : 'none',
                  transition: 'transform 0.2s',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </NotificationHeader>

        {loading && <LinearProgress />}

        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {error}
            </Alert>
          </Box>
        )}

        {!loading && (!notifications || notifications.length === 0) ? (
          <EmptyStateContainer>
            <NotificationsNoneIcon />
            <Typography color="text.secondary" variant="h6">
              Nenhuma notificação
            </Typography>
            <Typography color="text.disabled" variant="body2" sx={{ mt: 1 }}>
              Você está em dia! 🎉
            </Typography>
          </EmptyStateContainer>
        ) : (
          <NotificationContent>
            {Array.isArray(notifications) && notifications.map((notification, index) => (
              <Grow 
                key={notification.id} 
                in={true} 
                timeout={300 + index * 50}
                style={{ transformOrigin: '0 0 0' }}
              >
                <NotificationItem
                  className={!notification.isRead ? 'unread' : ''}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!notification.isRead) {
                      handleMarkAsReadLocal(notification.id);
                    }
                  }}
                >
                  <Stack spacing={1.5} sx={{ width: '100%', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 'auto', mt: 0.5, flexShrink: 0 }}>
                        <Box sx={{ position: 'relative' }}>
                          {getNotificationIcon(notification.type)}
                          {!notification.isRead && (
                            <CircleIcon 
                              sx={{ 
                                position: 'absolute',
                                top: -4,
                                right: -4,
                                fontSize: 8,
                                color: theme.palette.error.main
                              }} 
                            />
                          )}
                        </Box>
                      </ListItemIcon>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography 
                            variant="subtitle2" 
                            fontWeight={!notification.isRead ? 700 : 500}
                            sx={{ 
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {String(notification.title || '')}
                          </Typography>
                          <Chip 
                            label={getNotificationTypeLabel(notification.type)}
                            size="small"
                            sx={{ 
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: theme.palette.mode === 'dark' 
                                ? alpha(theme.palette.primary.main, 0.2)
                                : alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                              border: theme.palette.mode === 'dark' 
                                ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                : 'none',
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 0.5,
                            lineHeight: 1.5,
                            wordBreak: 'break-word'
                          }}
                        >
                          {String(notification.message || '')}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.disabled"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {formatNotificationDate(notification.createdAt)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Action buttons for invites */}
                    {(notification.projectInvite || notification.boardInvite) && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={processingId === notification.id ? <CircularProgress size={14} /> : <CheckIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationActionLocal(notification.id, 'accept');
                          }}
                          disabled={processingId === notification.id}
                          sx={{ 
                            textTransform: 'none',
                            borderRadius: 2,
                            px: 2,
                            boxShadow: 1,
                            '&:hover': {
                              boxShadow: 2,
                            }
                          }}
                        >
                          Aceitar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationActionLocal(notification.id, 'reject');
                          }}
                          disabled={processingId === notification.id}
                          sx={{ 
                            textTransform: 'none',
                            borderRadius: 2,
                            px: 2
                          }}
                        >
                          Recusar
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </NotificationItem>
              </Grow>
            ))}
          </NotificationContent>
        )}
      </StyledMenu>
    </>
  );
};

export default Notifications;
